const SIGNED_NUMBER = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)';
const UNSIGNED_NUMBER = '(?:\\d+(?:\\.\\d*)?|\\.\\d+)';
const DECIMAL_RE = new RegExp(`^(${SIGNED_NUMBER})\\s*°?\\s*$`);
const DEGREES_MINUTES_RE = new RegExp(`^(${SIGNED_NUMBER})(?:\\s*°\\s*|\\s+)(${UNSIGNED_NUMBER})\\s*'?\\s*$`);
const DEGREES_MINUTES_SECONDS_RE = new RegExp(`^(${SIGNED_NUMBER})(?:\\s*°\\s*|\\s+)(${UNSIGNED_NUMBER})(?:\\s*'\\s*|\\s+)(${UNSIGNED_NUMBER})\\s*"?\\s*$`);
function splitCoordinateParts(raw) {
    const cleaned = raw.trim().replace(/[()]/g, '');
    if (!cleaned)
        return null;
    if (cleaned.includes(',')) {
        const parts = cleaned.split(',').map((part) => part.trim());
        if (parts.length !== 2 || parts.some((part) => !part))
            return null;
        return [parts[0], parts[1]];
    }
    const letters = Array.from(cleaned.matchAll(/[NSEW]/gi));
    if (letters.length !== 2)
        return null;
    const firstIndex = letters[0].index;
    const secondIndex = letters[1].index;
    if (firstIndex === undefined || secondIndex === undefined)
        return null;
    const trimmed = cleaned.trim();
    const startsWithHemisphere = /^[NSEW]/i.test(trimmed);
    const endsWithHemisphere = /[NSEW]$/i.test(trimmed);
    if (!startsWithHemisphere && endsWithHemisphere) {
        return [cleaned.slice(0, firstIndex + 1), cleaned.slice(firstIndex + 1)];
    }
    if (startsWithHemisphere && !endsWithHemisphere) {
        return [cleaned.slice(0, secondIndex), cleaned.slice(secondIndex)];
    }
    // A suffix component followed by a prefix component ("32 N W 103") is
    // still unambiguous. A prefix followed by a suffix is not: without a comma,
    // there is no reliable boundary between their numeric portions.
    if (!startsWithHemisphere && !endsWithHemisphere) {
        const separator = cleaned.slice(firstIndex + 1, secondIndex);
        if (!/^\s*$/.test(separator))
            return null;
        return [cleaned.slice(0, firstIndex + 1), cleaned.slice(secondIndex)];
    }
    return null;
}
function parseCoordinatePart(raw) {
    const hemisphereMatches = raw.match(/[NSEW]/gi) || [];
    if (hemisphereMatches.length > 1)
        return null;
    const hemisphere = hemisphereMatches[0]?.toUpperCase() || null;
    const angle = raw
        .replace(/[NSEW]/gi, '')
        .replace(/[′’]/g, "'")
        .replace(/[″”]/g, '"')
        .trim();
    let degrees;
    let minutes = 0;
    let seconds = 0;
    const dmsMatch = angle.match(DEGREES_MINUTES_SECONDS_RE);
    const dmMatch = angle.match(DEGREES_MINUTES_RE);
    const decimalMatch = angle.match(DECIMAL_RE);
    if (dmsMatch) {
        degrees = Number(dmsMatch[1]);
        minutes = Number(dmsMatch[2]);
        seconds = Number(dmsMatch[3]);
    }
    else if (dmMatch) {
        degrees = Number(dmMatch[1]);
        minutes = Number(dmMatch[2]);
    }
    else if (decimalMatch) {
        degrees = Number(decimalMatch[1]);
    }
    else {
        return null;
    }
    if (![degrees, minutes, seconds].every(Number.isFinite))
        return null;
    if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60)
        return null;
    const magnitude = Math.abs(degrees) + minutes / 60 + seconds / 3600;
    let value = degrees < 0 || Object.is(degrees, -0) ? -magnitude : magnitude;
    let axis = null;
    if (hemisphere) {
        axis = hemisphere === 'N' || hemisphere === 'S' ? 'latitude' : 'longitude';
        value = hemisphere === 'S' || hemisphere === 'W' ? -magnitude : magnitude;
    }
    return { value: value === 0 ? 0 : value, axis };
}
function formatCoordinate(value) {
    const rounded = Number(value.toFixed(6));
    return String(Object.is(rounded, -0) ? 0 : rounded);
}
/**
 * Parse a coordinate pair pasted as decimal degrees, degrees/minutes, or DMS.
 * Hemisphere letters determine both axis and sign when present.
 */
export function parseCoordinatesText(raw) {
    if (typeof raw !== 'string')
        return null;
    const parts = splitCoordinateParts(raw);
    if (!parts)
        return null;
    const first = parseCoordinatePart(parts[0]);
    const second = parseCoordinatePart(parts[1]);
    if (!first || !second)
        return null;
    let latitude;
    let longitude;
    if (first.axis && second.axis) {
        if (first.axis === second.axis)
            return null;
        latitude = first.axis === 'latitude' ? first.value : second.value;
        longitude = first.axis === 'longitude' ? first.value : second.value;
    }
    else if (first.axis) {
        latitude = first.axis === 'latitude' ? first.value : second.value;
        longitude = first.axis === 'longitude' ? first.value : second.value;
    }
    else if (second.axis) {
        latitude = second.axis === 'latitude' ? second.value : first.value;
        longitude = second.axis === 'longitude' ? second.value : first.value;
    }
    else {
        latitude = first.value;
        longitude = second.value;
    }
    if (latitude < -90 || latitude > 90)
        return null;
    if (longitude < -180 || longitude > 180)
        return null;
    return {
        latitude,
        longitude,
        normalized: `${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`,
    };
}
//# sourceMappingURL=coordinates.js.map